import React, { useCallback, useMemo, useState } from 'react';

import { gridVisibleSortedRowIdsSelector } from '@mui/x-data-grid-premium';
import { type GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { ArrowDownToLine, FoldVertical, UnfoldVertical } from 'lucide-react';

import Button from '../../common/Button';
import { type OutputSChemaDataTable } from '../../common/dataTypes';
import { useGetDistinguishExpenseSets } from '../../hooks/tanstack/useUtilsHook';
import DataGridPremiumTable, {
  type CompareCellType,
} from '../DataGridPremiumTable';

type ModelOutputProps = {
  apiRef: React.MutableRefObject<GridApiPremium>;
  handleExportToExcel: () => void;
  dataTable: OutputSChemaDataTable;
  isLoading?: boolean;
  errorMessage?: string;
  report: string;
  modelOutputData?:
    | Record<string, (string | number | CompareCellType)[]>
    | undefined;
};

const ModelOutput = ({
  apiRef,
  report,
  isLoading,
  handleExportToExcel,
  dataTable,
  modelOutputData,
  errorMessage,
}: ModelOutputProps) => {
  const [isExpandAll, setIsExpandAll] = useState(true);

  const treeData = useMemo(() => {
    let result;

    if (report === 'financials') {
      result = {
        headerName: 'Expense',
        groupColumns: ['expense_group_name', 'expense_name', 'crew_type'],
      };
    } else if (report === 'kpis') {
      result = {
        headerName: 'KPIs',
        groupColumns: ['stats_name', 'crew_type'],
      };
    } else if (report === 'average_expense') {
      result = {
        headerName: 'Average Expense',
        groupColumns: ['expense_name', 'crew_type'],
      };
    }

    return result;
  }, [report]);

  const handleToggleGroups = useCallback(() => {
    if (!apiRef.current) {
      return;
    }

    const rowIds = apiRef.current.getAllRowIds();
    const visibleRowsIds = gridVisibleSortedRowIdsSelector(apiRef);

    let expand = true;

    if (rowIds.length === visibleRowsIds.length) {
      setIsExpandAll(false);
      expand = false;
    } else {
      setIsExpandAll(true);
      expand = true;
    }

    rowIds.forEach((rowId) => {
      apiRef.current.setRowChildrenExpansion(rowId, expand);
    });
  }, [apiRef, dataTable]);

  const actualDataColumns = new Set<string>(
    (modelOutputData?.['__meta__'] as { actual_data_columns?: string[] })
      ?.actual_data_columns ?? []
  );

  const { userDefinedExpenseSets } = useGetDistinguishExpenseSets();

  const customLabelModelOutputData = useMemo(
    () =>
      Object.entries(modelOutputData ?? {}).reduce(
        (acc, [key, value]) => {
          if (key === 'expense_name') {
            acc[key] = (value as string[]).map((item: string) =>
              userDefinedExpenseSets?.map((u) => u.name).includes(item)
                ? `${item} (custom)`
                : item
            );
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, (string | number | CompareCellType)[]>
      ),
    [modelOutputData, userDefinedExpenseSets]
  );

  return (
    <>
      {treeData && (
        <div className="flex items-center justify-end gap-1">
          <Button
            onClick={handleExportToExcel}
            variant="outlined_blue"
            disabled={!modelOutputData || isLoading}
            className="space-x-1 font-semibold"
          >
            <ArrowDownToLine />
            <label>Export to Excel</label>
          </Button>
          <Button
            disabled={isLoading}
            onClick={handleToggleGroups}
            variant="outlined"
          >
            {isExpandAll ? (
              <div className="flex items-center gap-1">
                <FoldVertical strokeWidth={1.5} />
                <p>Collapse All</p>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <UnfoldVertical strokeWidth={1.5} />
                <p>Expand All</p>
              </div>
            )}
          </Button>
        </div>
      )}
      <div className="py-4">
        {dataTable && (
          <DataGridPremiumTable
            treeData={treeData ?? undefined}
            apiRef={apiRef}
            actualDataColumns={actualDataColumns}
            columns={dataTable.columns.map((c) => ({
              label: c.display_name,
              name: c.name,
              is_pinned: c.is_pinned,
              data_type: c.data_type,
              styles: c.styles,
            }))}
            placeholder={
              errorMessage ??
              (isLoading
                ? 'The system is running your comparison...'
                : 'Click run comparison to see the difference')
            }
            isLoading={!!isLoading}
            data={customLabelModelOutputData}
          />
        )}
      </div>
    </>
  );
};

export default ModelOutput;
